import AXIOS from "@/axios";

import { APIResponse } from "@/types";
import {
  CourseModelList,
  CourseModel,
  CourseQuery,
  CreateCoursePayload,
  UpdateCoursePayload,
  UpdateCourseActivePayload,
  UnitScormFile,
  UnitUploadMeta,
  UnitUploadResponse,
  UpdateScormData,
  DownloadCourseQuery,
  UpdateScormStatus,
} from "@/types/course.model";
import { removeEmptyProps } from "@/utils/constants";
import moment from "moment";
import { normalizeCourseView } from "./course.mappers";

export type CsViewType = "course" | "class";

/**
 * Get all courses with pagination and filtering
 */
export const getAllCoursesService = async (
  query: CourseQuery
): Promise<{
  data: APIResponse<CourseModelList[]>;
}> => {
  if (query.fromDate && query.toDate) {
    query.fromDate = moment(new Date(query.fromDate).toISOString()).format(
      "DD-MM-YYYY"
    );
    query.toDate = moment(new Date(query.toDate).toISOString()).format(
      "DD-MM-YYYY"
    );
  }

  return AXIOS.get<APIResponse<CourseModelList[]>>("/cs/api/v1/list", {
    params: removeEmptyProps(query),
  });
};

const LIST_LOOKUP_PAGE_SIZE = 100;
const LIST_LOOKUP_MAX_PAGES = 50;

/** List-row metadata when router state / view omit `is_published` (paginated scan) */
export const getCourseListItemByIdService = async (
  gradeId: string
): Promise<CourseModelList | null> => {
  let page = 1;

  while (page <= LIST_LOOKUP_MAX_PAGES) {
    const response = await getAllCoursesService({
      page,
      pageSize: LIST_LOOKUP_PAGE_SIZE,
    });
    const rows = response.data?.data ?? [];
    const match = rows.find((row) => row.id === gradeId);
    if (match) return match;

    const totalPages = response.data?.pageInfo?.total_pages ?? 1;
    if (page >= totalPages) return null;
    page += 1;
  }

  return null;
};

/**
 * Get a single course or class offering by ID (CS view).
 * @param type - `course` (default) or `class` for class offerings
 */
export const getCourseService = async (
  id: string,
  studentId: string | null,
  type: CsViewType = "course"
): Promise<{
  data: APIResponse<CourseModel>;
}> => {
  const response = await AXIOS.get<APIResponse<CourseModel>>(
    `/cs/api/v1/view/${id}`,
    {
      params: removeEmptyProps({ studentId, type }),
    }
  );
  return {
    ...response,
    data: {
      ...response.data,
      data: normalizeCourseView(response.data.data),
    },
  };
};

/** POST /cs/api/courses/upload-unit/ — unit SCORM zip */
export const uploadCourseUnitService = async (
  file: File,
  meta: UnitUploadMeta
) => {
  const formData = new FormData();
  formData.append("file", file);
  formData.append("course_name", meta.course_name);
  formData.append("lesson_name", meta.lesson_name);
  formData.append("unit_name", meta.unit_name);

  return AXIOS.post<APIResponse<UnitUploadResponse>>(
    "/cs/api/courses/upload-unit/",
    formData,
    {
      headers: { "Content-Type": "multipart/form-data" },
    }
  );
};

/** POST /cs/api/courses/ — create course */
export const createCourseService = async (payload: CreateCoursePayload) => {
  return AXIOS.post<APIResponse<unknown>>("/cs/api/courses/", payload);
};

/**
 * PUT /cs/api/courses/:grade_id/update/
 * Backend accepts partial bodies on the same endpoint, e.g. `{ is_active }` only
 * or full `{ grade, is_published, is_active, terms }`.
 */
export const updateCourseService = async (
  gradeId: string,
  payload: UpdateCoursePayload | UpdateCourseActivePayload
) => {
  return AXIOS.put<APIResponse<unknown>>(
    `/cs/api/courses/${gradeId}/update/`,
    payload
  );
};

export const getUnitScormFileService = async (id: string) => {
  return AXIOS.get<UnitScormFile>(`/cs/api/v1/scorm/${id}`);
};

export const updateScormDataService = async (
  id: string,
  payload: Omit<UpdateScormData, "id">
) => {
  return AXIOS.put<APIResponse<UpdateScormData>>(
    `/cs/api/v1/scorm/update/${id}`,
    payload
  );
};

export const updateScormStatusService = async (payload: UpdateScormStatus) => {
  return AXIOS.post<APIResponse<{ id: string }>>(`/cs/api/v1/webhook`, payload);
};

export const downloadCourseService = async (
  type: "pdf" | "xl" | "csv",
  params: DownloadCourseQuery
) => {
  if (params.fromDate && params.toDate) {
    params.fromDate = moment(new Date(params.fromDate).toISOString()).format(
      "DD-MM-YYYY"
    );
    params.toDate = moment(new Date(params.toDate).toISOString()).format(
      "DD-MM-YYYY"
    );
  }

  return AXIOS.get(`/cs/api/v1/dwnld/${type}`, {
    params: removeEmptyProps(params),
    responseType: "blob",
  });
};

/** Active/inactive — PUT update with `{ is_active }` only */
export const toggleCourseActiveService = async (
  gradeId: string,
  isActive: boolean
) => {
  return updateCourseService(gradeId, { is_active: isActive });
};

export const deleteCourseService = async (id: string) => {
  return AXIOS.delete<APIResponse<{ id: string }>>(`/cs/api/v1/delete/${id}`);
};
